// Idle detection hook — locks app after configured inactivity period

import { useEffect, useRef } from 'react';
import { useLockStore } from '@/stores/lockStore';

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'touchstart', 'pointerdown'] as const;
const DEBOUNCE_MS = 1000;
const CHECK_INTERVAL_MS = 10_000;

export function useIdleDetection() {
  const { isLocked, pinEnabled, preferences, recordActivity, lock, lastActivityAt } = useLockStore();
  const debounceRef = useRef(0);

  // Listen for user activity and debounce updates
  useEffect(() => {
    if (!pinEnabled || isLocked) return;

    const handleActivity = () => {
      const now = Date.now();
      if (now - debounceRef.current < DEBOUNCE_MS) return;
      debounceRef.current = now;
      recordActivity();
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, handleActivity, { passive: true });
    }

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, handleActivity);
      }
    };
  }, [pinEnabled, isLocked, recordActivity]);

  // Interval check for idle timeout
  useEffect(() => {
    if (!pinEnabled || isLocked) return;

    const timeoutMs = preferences.idleTimeoutMinutes * 60 * 1000;

    const interval = setInterval(() => {
      if (Date.now() - lastActivityAt >= timeoutMs) {
        lock();
      }
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [pinEnabled, isLocked, preferences.idleTimeoutMinutes, lastActivityAt, lock]);
}
